import get from 'lodash/get';
import has from 'lodash/has';
import intersection from 'lodash/intersection';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import partition from 'lodash/partition';
import sortBy from 'lodash/sortBy';
import { createSelector } from 'reselect';

import {
  UPDATE_MEAL_FILTER_V3,
  UPDATE_MEAL_FILTER,
  DASHBOARD_CLEAR_ALL_FILTERS,
  CLEAR_MEAL_FILTER,
  SET_FILTER_CONTEXT,
} from 'actions/mealFilters';
import {
  VEGETARIAN,
  CALORIE_CONSCIOUS,
  CARB_CONSCIOUS,
  GLUTEN_SMART,
  KETO_FRIENDLY,
  PALEO_FRIENDLY,
  HIGH_PROTEIN,
  FIBER_RICH,
  PESCATARIAN,
  NOT_SPICY,
  MILD,
  MEDIUM,
  SPICY,
  EASY,
  INTERMEDIATE,
  EXPERT,
  DESSERT,
  SIDES,
  MEDITERRANEAN,
  EASY_PREP,
  GLP_SMART,
  GLP_SMART_NAME,
  GLP_FRIENDLY,
  GLP_FRIENDLY_NAME,
  WEIGHT_WATCHERS,
  WEIGHT_WATCHERS_NAME,
  BUNDLED_FILTERS,
} from 'lib/constants';
import { getMealTypeGroupIdentityFromKey, isMealTypeGroupKey } from 'helpers/mealTypeGroupKeys';
import * as fromCookbook from 'reducers/cookbook';
import * as fromDashboard from 'selectors/dashboard';

import * as fromSendABox from './sendABox';

export const FIVE_STAR_RATING = 5;

export const FILTER_SUFFIX = 'Filter';

export const COOK_TIME_FILTER = 'cookTimeFilter';
export const MEAL_TYPE_FILTER = 'mealTypeFilter';
export const DIET_PREF_FILTER = 'dietaryPreferenceFilter';
export const AVOIDANCE_FILTER = 'avoidanceFilter';
export const SPICE_LEVEL_FILTER = 'spiceLevelFilter';
export const PRIMARY_PROTEIN_FILTER = 'primaryProteinFilter';
export const MEAL_BADGE_FILTER = 'mealBadgeFilter';
export const MY_FAVORITES_FILTER = 'myFavoritesFilter';
export const DIFFICULTY_LEVEL_FILTER = 'difficultyLevelFilter';
export const MEAL_LABEL_FILTER = 'mealLabelFilter';
export const DISPOSABLE_ITEMS_FILTER = 'disposableItemsFilter';
export const PRODUCT_TYPE_FILTER = 'productTypeFilter';
export const NET_WEIGHT_FILTER = 'netWeightFilter';

export const FILTER_CONTEXT = 'filterContext';

export const mapFilterNames = {
  [COOK_TIME_FILTER]: 'Cook Time',
  [MEAL_TYPE_FILTER]: 'Meal Type',
  [DIET_PREF_FILTER]: 'Dietary Preferences',
  [AVOIDANCE_FILTER]: 'Dislikes',
  [SPICE_LEVEL_FILTER]: 'Spice Level',
  [PRIMARY_PROTEIN_FILTER]: 'Protein',
  [MEAL_BADGE_FILTER]: 'Featured Meals',
  [MY_FAVORITES_FILTER]: 'Favorites',
  [DIFFICULTY_LEVEL_FILTER]: 'Difficulty',
  [MEAL_LABEL_FILTER]: 'Meal Label',
  [DISPOSABLE_ITEMS_FILTER]: 'Oven-Ready',
  [PRODUCT_TYPE_FILTER]: 'Premium',
  [NET_WEIGHT_FILTER]: 'Big Portions',
};
export const filterToDisplayName = {
  [CALORIE_CONSCIOUS]: 'Calorie-Conscious',
  [CARB_CONSCIOUS]: 'Carb-Conscious',
  [VEGETARIAN]: 'Vegetarian',
  [GLUTEN_SMART]: 'Gluten-Smart',
  [KETO_FRIENDLY]: 'Keto-Friendly',
  [PALEO_FRIENDLY]: 'Paleo-Friendly',
  [HIGH_PROTEIN]: 'Protein-Packed',
  [FIBER_RICH]: 'Fiber-Rich',
  [PESCATARIAN]: 'Pescatarian',
  [EASY_PREP]: 'Easy Prep',
  [MEDITERRANEAN]: 'Mediterranean',
  [GLP_FRIENDLY_NAME.toLowerCase()]: GLP_FRIENDLY,
  [GLP_SMART_NAME]: GLP_SMART,
  [WEIGHT_WATCHERS_NAME]: WEIGHT_WATCHERS,
  beef: 'Beef',
  fish: 'Fish',
  milk: 'Milk',
  mushrooms: 'Mushrooms',
  peanuts: 'Peanuts',
  pork: 'Pork',
  poultry: 'Poultry',
  sesame: 'Sesame',
  shellfish: 'Shellfish',
  soy: 'Soy',
  'tree nuts': 'Tree Nuts',
  wheat: 'Wheat',
  [NOT_SPICY]: NOT_SPICY,
  [MILD]: MILD,
  [MEDIUM]: MEDIUM,
  [SPICY]: SPICY,
  [EASY]: EASY,
  [INTERMEDIATE]: INTERMEDIATE,
  [EXPERT]: EXPERT,
};

export const initialState = {
  [COOK_TIME_FILTER]: {},
  [MEAL_TYPE_FILTER]: {},
  [DIET_PREF_FILTER]: {},
  [AVOIDANCE_FILTER]: {},
  [SPICE_LEVEL_FILTER]: {},
  [PRIMARY_PROTEIN_FILTER]: {},
  [MEAL_BADGE_FILTER]: {},
  [MY_FAVORITES_FILTER]: {},
  [DIFFICULTY_LEVEL_FILTER]: {},
  [MEAL_LABEL_FILTER]: {},
  [DISPOSABLE_ITEMS_FILTER]: {},
  [PRODUCT_TYPE_FILTER]: {},
  [NET_WEIGHT_FILTER]: {},
  [FILTER_CONTEXT]: { menuSlug: null, stageStep: null },
};

export default (state = initialState, action) => {
  switch (action.type) {
    case SET_FILTER_CONTEXT:
      return {
        ...state,
        filterContext: {
          menuSlug: action.menuSlug,
          stageStep: action.stageStep,
        },
      };
    case UPDATE_MEAL_FILTER_V3:
    case UPDATE_MEAL_FILTER:
      return {
        ...state,
        [action.filter]: action.value,
      };
    case CLEAR_MEAL_FILTER:
      return {
        ...state,
        [action.filter]: initialState[action.filter],
      };
    case DASHBOARD_CLEAR_ALL_FILTERS:
      return {
        ...initialState,
        [FILTER_CONTEXT]: state[FILTER_CONTEXT],
      };
    default:
      return state;
  }
};

export const filterIsApplied = filter => !isEmpty(filter);

export const selectMealFilters = state => state.mealFilters;
export const selectFilterContext = state => selectMealFilters(state)[FILTER_CONTEXT];

export const selectTotalFiltersApplied = state => {
  const mealFiltersState = selectMealFilters(state);
  return Object.keys(mealFiltersState).reduce((filterTotal, key) => {
    if (key === FILTER_CONTEXT || !key.endsWith(FILTER_SUFFIX)) return filterTotal;

    const filterValue = mealFiltersState[key];
    if (filterValue == null) return filterTotal;

    if (isObject(filterValue)) {
      let count = 0;
      let totalValues = Object.keys(filterValue);
      if (key === MEAL_TYPE_FILTER) {
        const groupKeys = totalValues.filter(isMealTypeGroupKey);
        if (groupKeys.length) {
          return filterTotal + groupKeys.length;
        }
      }
      BUNDLED_FILTERS.forEach(bundle => {
        if (bundle.every(filter => filterValue[filter])) {
          totalValues = totalValues.filter(key => !bundle.includes(key));
          count++;
        }
      });
      return filterTotal + totalValues.length + count;
    }

    return filterTotal + 1;
  }, 0);
};

export const selectFiltersApplied = state => {
  const mealFiltersState = selectMealFilters(state);
  return Object.entries(mealFiltersState).reduce((selectedFilters, [key, filterValue]) => {
    if (key === FILTER_CONTEXT || !key.endsWith(FILTER_SUFFIX) || filterValue == null) {
      return selectedFilters;
    }

    if (isObject(filterValue)) {
      return selectedFilters.concat(
        Object.entries(filterValue)
          .filter(([, isSelected]) => isSelected)
          .map(([value]) => value)
      );
    }

    return filterValue ? selectedFilters.concat(filterValue) : selectedFilters;
  }, []);
};

export const selectFilterLabel = (state, filter) => {
  const mealFiltersState = selectMealFilters(state);
  const selectedFilter = mealFiltersState[filter];
  if (isObject(selectedFilter)) {
    const allKeys = Object.keys(selectedFilter);
    const groupIdentities = allKeys.map(getMealTypeGroupIdentityFromKey).filter(Boolean);
    const filters = groupIdentities.length ? groupIdentities : allKeys;
    if (filters.length === 1) {
      const filterValue = filters[0];
      return (
        (filter === COOK_TIME_FILTER && `${filterValue} Min`) ||
        filterToDisplayName[filterValue] ||
        filterValue
      );
    } else if (filters.length > 1) {
      return `${mapFilterNames[filter]} · ${filters.length}`;
    }
  }
  return mapFilterNames[filter];
};

export const selectMealFilter = (state, filter) => get(selectMealFilters(state), filter);

const getMealsForMenu = (state, menuSlug, stageStep) => {
  return fromDashboard.selectNonDonationMealsForMenu(state, menuSlug, stageStep);
};
const getMealsInBasket = (state, menuSlug) => {
  return fromDashboard.selectMealsForBasket(state, menuSlug);
};
const getFavorites = state => fromCookbook.selectFavorites(state);
const getCompletedReviews = state => fromDashboard.selectCompletedReviews(state);

export const selectFilteredMealsForBasket = createSelector(
  [getMealsForMenu, getMealsInBasket, selectMealFilters, getFavorites, getCompletedReviews],
  (meals, selectedMeals, filters, favorites, reviews) => {
    let [matches, notMatches] = partition(meals, meal =>
      checkMealFilters(meal, filters, favorites, reviews)
    );

    const selectedMealIds = selectedMeals.map(selectedMeal => selectedMeal.id);

    const sortMealsbySelected = meal => {
      return !selectedMealIds.includes(meal.id);
    };

    matches = sortBy(matches, sortMealsbySelected);
    notMatches = sortBy(notMatches, sortMealsbySelected);
    return { matches, notMatches };
  }
);

export const selectFilteredMealsForSendABox = createSelector(
  fromSendABox.selectMealsForMenu,
  selectMealFilters,
  state => state.user,
  (meals, filters, user) => {
    let [matches, notMatches] = partition(meals, meal =>
      checkMealFilters(meal, filters, user?.favorites, user?.reviews)
    );

    return { matches, notMatches };
  }
);

export const checkCookTimeFilter = (meal, filter) => {
  const cookTimeMap = {
    '<5': 4,
    '60+': 61,
  };

  const filterValue = Object.keys(filter)[0];
  const maxCookTime = Number(cookTimeMap[meal.maxCookTime] || meal.maxCookTime);

  if (Number(filterValue) === 0) return !meal.maxCookTime || maxCookTime === 0;
  if (isNaN(maxCookTime)) return false;

  if (!filterValue.includes('-')) {
    return maxCookTime <= Number(filterValue);
  }

  const [startNum, endNum] = filterValue.split('-');
  return maxCookTime >= Number(startNum) && maxCookTime <= Number(endNum);
};

const checkMealTypeFilter = (meal, filter) => {
  const { menuCategoryForMeal, primaryLabel, primaryLabelData, productTypeName } = meal;
  const { labelText } = primaryLabelData || {};

  if (productTypeName && !isMealTypeGroupKey(productTypeName) && filter[productTypeName] === true) {
    return true;
  }

  if (has(filter, SIDES)) {
    return menuCategoryForMeal === SIDES.toLowerCase();
  }

  if (has(filter, DESSERT) && meal.subtitle == 'dessert duo') {
    return filter[DESSERT] === true;
  }

  return filter[primaryLabel || labelText] === true;
};

const checkMealLabelFilter = (meal, filter) => {
  const label = meal.mealLabel?.label?.trim();
  return Boolean(label && filter[label]);
};

const checkDisposableItemsFilter = (meal, filter) => {
  return intersection(meal.disposableItems || [], Object.keys(filter)).length > 0;
};

const checkProductTypeFilter = (meal, filter) => {
  return Boolean(meal.productTypeName && filter[meal.productTypeName]);
};

const checkNetWeightFilter = (meal, filter) => {
  const threshold = Number(Object.keys(filter)[0]);
  const netWeightOz = Number(meal.netWeightOz);
  if (isNaN(threshold) || isNaN(netWeightOz)) return false;
  return netWeightOz >= threshold;
};

const checkFilter = (meal, filter, filterName, favorites, reviews) => {
  switch (filterName) {
    case COOK_TIME_FILTER:
      return checkCookTimeFilter(meal, filter);
    case MEAL_TYPE_FILTER:
      return checkMealTypeFilter(meal, filter);
    case PRIMARY_PROTEIN_FILTER:
      return intersection(meal.primaryProteinNames, Object.keys(filter)).length > 0;
    case DIET_PREF_FILTER:
      // Make casing consistent with toLowerCase because the filterableTags are capitalized (stems
      // from dietary preference model FRIENDLY_NAMES), but the quick filters here use lowercase
      return (
        intersection(
          meal.filterableTags?.map(t => t.toLowerCase()),
          Object.keys(filter)?.map(t => t.toLowerCase())
        ).length > 0
      );
    case AVOIDANCE_FILTER:
      return intersection(meal.avoidances, Object.keys(filter)).length === 0;
    case SPICE_LEVEL_FILTER:
      return filter[meal.spiceLevel] === true;
    case DIFFICULTY_LEVEL_FILTER:
      return filter[meal.difficultyLevel] === true;
    case MEAL_BADGE_FILTER:
      return meal.mealBadge && filter[meal.mealBadge.name] === true;
    case MEAL_LABEL_FILTER:
      return checkMealLabelFilter(meal, filter);
    case DISPOSABLE_ITEMS_FILTER:
      return checkDisposableItemsFilter(meal, filter);
    case PRODUCT_TYPE_FILTER:
      return checkProductTypeFilter(meal, filter);
    case NET_WEIGHT_FILTER:
      return checkNetWeightFilter(meal, filter);
    case MY_FAVORITES_FILTER:
      return (
        Object.keys(favorites).includes(meal.mealConcept) ||
        !!reviews
          ?.filter(review => review.recipeRating === FIVE_STAR_RATING)
          ?.find(review => review.mealConcept === meal.mealConcept)
      );
    default:
      return false;
  }
};

export const checkMealFilters = (meal, filters, favorites = {}, reviews = []) => {
  let mealIsInFilterGroup = true;

  [
    COOK_TIME_FILTER,
    MEAL_TYPE_FILTER,
    PRIMARY_PROTEIN_FILTER,
    DIET_PREF_FILTER,
    AVOIDANCE_FILTER,
    SPICE_LEVEL_FILTER,
    DIFFICULTY_LEVEL_FILTER,
    MEAL_BADGE_FILTER,
    MEAL_LABEL_FILTER,
    DISPOSABLE_ITEMS_FILTER,
    PRODUCT_TYPE_FILTER,
    NET_WEIGHT_FILTER,
    MY_FAVORITES_FILTER,
  ].forEach(filterName => {
    if (mealIsInFilterGroup === true) {
      if (filters[filterName] && !isEmpty(filters[filterName])) {
        mealIsInFilterGroup = checkFilter(
          meal,
          filters[filterName],
          filterName,
          favorites,
          reviews
        );
      }
    }
  });

  return mealIsInFilterGroup;
};
